import { createSlice } from "@reduxjs/toolkit";
import { showNotification } from "../../components/Notification";
import { extractUserFromToken, getRoleFromRoleId } from "../../uitls/Utils";
import {
  addNewUser,
  deleteUserById,
  getUserById,
  getAllUsers,
  getManagersByRoleId,
  signIn,
  mfaSetup,
  mfaVerifyAndEnable,
  mfaVerify,
  mfaLoginWithTotp,
  updateUser,
  userSignUp,
  sendOTP,
  resetPassword,
  filterUsersByDept_and_Role,
  getManagerByDept,
  validateChangePassword,
  uploadMultipleEmployees,
  resetMFA,
  getPasswordRequirements,
} from "../../api/userApi";
import axiosHandler from "../../api/axiosHandler";

const loadUserFromStorage = () => {
  try {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error("Error loading user from localStorage:", error);
    return null;
  }
};

const loadTokenFromStorage = () => {
  return localStorage.getItem("token") || null;
};

const loadRoleIdFromStorage = () => {
  return localStorage.getItem("roleId") || null;
};

const loadMfaRequiredFromStorage = () => {
  return localStorage.getItem("mfaRequired") === 'true';
};

const loadMfaVerifiedFromStorage = () => {
  return localStorage.getItem("mfaVerified") === 'true';
};

const initialState = {
  isAuthenticated: !!loadTokenFromStorage(),
  loading: false,
  roleId: loadRoleIdFromStorage(),
  user: loadUserFromStorage(),
  userById: null,
  token: loadTokenFromStorage(),
  error: null,
  signUpError: null,
  usersByDept_and_role: [],
  managerBydept: [],
  otpSent: false,
  tempEmail: null,
  allUsers: [],
  managerList: [],
  status: "idle",
  mfaRequired: loadMfaRequiredFromStorage(),
  mfaVerified: loadMfaVerifiedFromStorage(),
  passwordRequirements: [],
};

const userSlice = createSlice({
  name: "userState",
  initialState,
  reducers: {
logout(state) {
  Object.assign(state, {
    isAuthenticated: false,
    user: null,
    token: null,
    roleId: null,
    error: null,
    status: "idle",
    otpSent: false,
    tempEmail: null,
    mfaRequired: false,
    mfaVerified: false,
  });

  const itemsToRemove = [
    "token", "user", "roleId", "sidebarMenu", 
    "mfaRequired", "mfaVerified", "mfaEnabled"
  ];
  
  itemsToRemove.forEach(item => localStorage.removeItem(item));
  
  delete axiosHandler.defaults.headers["Authorization"];
  
  showNotification("info", "Your account has been logged out.");
},
    clearOtpState(state) {
      state.otpSent = false;
      state.tempEmail = null;
      state.error = null;
    },
    updateMfaStatus(state, action) {
      if (action.payload.mfaRequired !== undefined) {
        state.mfaRequired = action.payload.mfaRequired;
        localStorage.setItem("mfaRequired", action.payload.mfaRequired.toString());
      }
      if (action.payload.mfaVerified !== undefined) {
        state.mfaVerified = action.payload.mfaVerified;
        localStorage.setItem("mfaVerified", action.payload.mfaVerified.toString());
      }
    },
    setUser(state, action) {
      if (action.payload.token !== undefined) {
        state.token = action.payload.token;
        localStorage.setItem("token", action.payload.token);
      }
      if (action.payload.user !== undefined) {
        let userData = action.payload.user;
        
        if (userData && !userData.roleName) {
          const roleId = userData.userRoleId || userData.roleId || action.payload.roleId;
          if (roleId) {
            userData = { ...userData, roleName: getRoleFromRoleId(roleId) || "User" };
          } else if (action.payload.token) {
            const roleName = extractUserFromToken(action.payload.token)?.roleName || "User";
            userData = { ...userData, roleName };
          }
        }
        
        state.user = userData;
        localStorage.setItem("user", JSON.stringify(userData));
      }
      if (action.payload.roleId !== undefined) {
        state.roleId = action.payload.roleId;
        localStorage.setItem("roleId", action.payload.roleId);
      }
      if (action.payload.mfaRequired !== undefined) {
        state.mfaRequired = action.payload.mfaRequired;
        localStorage.setItem("mfaRequired", action.payload.mfaRequired.toString());
      }
      if (action.payload.mfaVerified !== undefined) {
        state.mfaVerified = action.payload.mfaVerified;
        localStorage.setItem("mfaVerified", action.payload.mfaVerified.toString());
      }
      if (action.payload.token) {
        state.isAuthenticated = true;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
  .addCase(signIn.fulfilled, (state, action) => {
  state.status = "succeeded";

  const { token, user, roleId, mfaEnabled } = action.payload;
  const mfaSetupData = action.payload?.mfaSetupResponse || {};
  const hasMfaSetupData = !!(mfaSetupData.qrCodeUrl || mfaSetupData.secret);
  
  const mfaRequired = mfaEnabled === true || hasMfaSetupData;
  
  state.isAuthenticated = !!(token && !mfaRequired);
  state.mfaRequired = mfaRequired;
  state.mfaVerified = false;

  let userData = user;
  if (!userData && token) {
    userData = extractUserFromToken(token);
  }

  if (userData && !userData.roleName && token) {
    const roleName = extractUserFromToken(token)?.roleName || "User";
    userData = { ...userData, roleName };
  }

  state.token = token || null;
  state.user = userData || null;
  state.roleId = roleId || userData?.roleId || null;
  state.loading = false;

  if (token) {
    localStorage.setItem("token", token);
    axiosHandler.defaults.headers["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem("token");
    delete axiosHandler.defaults.headers["Authorization"];
  }
  
  if (userData) {
    localStorage.setItem("user", JSON.stringify(userData));
  } else {
    localStorage.removeItem("user");
  }
  
  if (state.roleId) {
    localStorage.setItem("roleId", state.roleId);
  } else {
    localStorage.removeItem("roleId");
  }
  
  localStorage.setItem("mfaRequired", mfaRequired.toString());
  localStorage.setItem("mfaVerified", "false");

  if (token && !mfaRequired) {
    showNotification("success", action.payload.message || "Login Successful");
  } else if (mfaRequired) {
    showNotification("info", "MFA verification required");
  }
})
.addCase(signIn.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.loading = false;
        showNotification(
          "error",
          action.payload?.message ||
            "Failed to complete the request, error occurred"
        );
      })

      .addCase(mfaLoginWithTotp.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(mfaLoginWithTotp.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isAuthenticated = true;
        state.mfaRequired = true;
        state.mfaVerified = true;

        let userData = action.payload.user;
        const tokenData = action.payload.token;
        const roleIdData = action.payload.roleId;

        if (userData && !userData.roleName) {
          if (tokenData) {
            const roleName = extractUserFromToken(tokenData)?.roleName || "User";
            userData = { ...userData, roleName };
          } else if (roleIdData) {
            const roleName = getRoleFromRoleId(roleIdData) || "User";
            userData = { ...userData, roleName };
          }
        }

        state.user = userData || null;
        state.token = tokenData || null;
        state.roleId = roleIdData || null;

        // Update localStorage
        if (userData) {
          localStorage.setItem("user", JSON.stringify(userData));
        }
        if (tokenData) {
          localStorage.setItem("token", tokenData);
        }
        if (roleIdData) {
          localStorage.setItem("roleId", roleIdData);
        }
        localStorage.setItem("mfaRequired", "true");
        localStorage.setItem("mfaVerified", "true");

        state.loading = false;
        showNotification(
          "success",
          action.payload.message || "Login Successful"
        );
      })
      .addCase(mfaLoginWithTotp.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.loading = false;
        showNotification(
          "error",
          action.payload?.message ||
            "Invalid verification code. Please try again."
        );
      })

      .addCase(addNewUser.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.signUpError = null;
        state.error = null;
      })
      .addCase(addNewUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allUsers = [...state.allUsers, action.payload?.data];
        state.loading = false;
        state.signUpError = null;
        showNotification("success", action.payload?.message);
      })
      .addCase(addNewUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.loading = false;
        showNotification("error", action.payload?.message || action.payload);
      })

      .addCase(userSignUp.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.signUpError = action.error?.message || action.payload;
        state.userInfo = null;
        state.loading = false;
        showNotification("error", action.payload?.message || action.payload);
      })
      .addCase(userSignUp.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.signUpError = null;
        state.error = null;
        state.userInfo = null;
      })
      .addCase(userSignUp.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userInfo = action.payload?.data;
        state.loading = false;
        state.error = null;
        state.signUpError = null;
        showNotification("success", action.payload?.message);
      })

      .addCase(getUserById.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.error = null;

        let userByIdData = action.payload;

        if (userByIdData && !userByIdData.roleName) {
          const token = state.token || localStorage.getItem("token");
          if (token) {
            const roleName = extractUserFromToken(token)?.roleName || "User";
            userByIdData = { ...userByIdData, roleName };
          }
        }

        state.userById = userByIdData;

        if (state.user && userByIdData?.id === state.user.id) {
          state.user = {
            ...state.user,
            ...userByIdData,
          };

          localStorage.setItem("user", JSON.stringify(state.user));
        }
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.loading = false;
      })

      .addCase(getAllUsers.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.allUsers = action.payload;
        state.loading = false;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.loading = false;
        showNotification("error", "Failed to fetch user details");
      })
      .addCase(getManagersByRoleId.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(getManagersByRoleId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.managerList = action.payload;
      })
      .addCase(getManagersByRoleId.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
        showNotification("error", action.payload?.message);
      })
      .addCase(updateUser.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;

        let updatedUserData = action.payload?.data || action.payload;

        if (updatedUserData && !updatedUserData.roleName) {
          const token = state.token || localStorage.getItem("token");
          if (token) {
            const roleName = extractUserFromToken(token)?.roleName || "User";
            updatedUserData = { ...updatedUserData, roleName };
          }
        }

        if (state.user && updatedUserData?.id === state.user.id) {
          state.user = {
            ...state.user,
            ...updatedUserData,
          };

          localStorage.setItem("user", JSON.stringify(state.user));
        }

        showNotification(
          "success",
          action.payload?.message || "Employee Details Updated Succesfully"
        );
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
        showNotification("error", action.payload?.message);
      })
      .addCase(deleteUserById.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.allUsers = state.allUsers.filter(
          (user) => user.id !== action.payload
        );
        showNotification("success", "Employee Details Deleted Successfully");
      })
      .addCase(deleteUserById.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
        showNotification("error", action.payload?.message);
      })
      .addCase(sendOTP.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.error = null;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
      })
      .addCase(resetPassword.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
      })
      .addCase(filterUsersByDept_and_Role.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(filterUsersByDept_and_Role.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.usersByDept_and_role = action.payload;
        state.error = null;
      })
      .addCase(filterUsersByDept_and_Role.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
      })
      .addCase(getManagerByDept.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(getManagerByDept.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.managerBydept = action.payload;
        state.error = null;
      })
      .addCase(getManagerByDept.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
      })

      .addCase(validateChangePassword.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(validateChangePassword.fulfilled, (state) => {
        state.status = "succeeded";
        state.loading = false;
        state.error = null;
      })
      .addCase(validateChangePassword.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
      })

      // MFA Setup
      .addCase(mfaSetup.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(mfaSetup.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
      })
      .addCase(mfaSetup.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.loading = false;
        showNotification(
          "error",
          action.payload?.message || "Failed to setup MFA"
        );
      })
      .addCase(mfaVerifyAndEnable.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(mfaVerifyAndEnable.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isAuthenticated = true;
        state.mfaRequired = true;
        state.mfaVerified = true;

        let userData = action.payload?.data?.user;
        const tokenData = action.payload?.data?.token;
        const roleIdData =
          action.payload?.data?.user?.userRoleId ||
          localStorage.getItem("roleId");

        if (userData && !userData.roleName) {
          if (tokenData) {
            const roleName = extractUserFromToken(tokenData)?.roleName || "User";
            userData = { ...userData, roleName };
          } else if (roleIdData) {
            const roleName = getRoleFromRoleId(roleIdData) || "User";
            userData = { ...userData, roleName };
          }
        }

        state.user = userData || null;
        state.token = tokenData || null;
        state.roleId = roleIdData || null;

        // Update localStorage
        if (userData) {
          localStorage.setItem("user", JSON.stringify(userData));
        }
        if (tokenData) {
          localStorage.setItem("token", tokenData);
        }
        if (roleIdData) {
          localStorage.setItem("roleId", roleIdData);
        }
        localStorage.setItem("mfaRequired", "true");
        localStorage.setItem("mfaVerified", "true");

        state.loading = false;
        showNotification(
          "success",
          action.payload?.message || "MFA enabled successfully"
        );
      })
      .addCase(mfaVerifyAndEnable.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.loading = false;
        showNotification(
          "error",
          action.payload?.message || "Invalid verification code"
        );
      })

      .addCase(uploadMultipleEmployees.pending, (state) => {
        state.uploading = true;
        state.uploadError = null;
        state.uploadResult = null;
      })
      .addCase(uploadMultipleEmployees.fulfilled, (state, action) => {
        state.uploading = false;
        state.uploadResult = action.payload;
        state.uploadError = null;
      })
      .addCase(uploadMultipleEmployees.rejected, (state, action) => {
        state.uploading = false;
        state.uploadError = action.payload || "Bulk upload failed";
        state.uploadResult = null;
      })
      .addCase(mfaVerify.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(mfaVerify.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.isAuthenticated = true;
        state.mfaRequired = true;
        state.mfaVerified = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.roleId = action.payload.roleId;
        state.loading = false;
        localStorage.setItem("mfaRequired", "true");
        localStorage.setItem("mfaVerified", "true");

        showNotification(
          "success",
          action.payload.message || "Login Successful"
        );
      })
      .addCase(mfaVerify.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message;
        state.loading = false;
        showNotification(
          "error",
          action.payload?.message || "Invalid verification code"
        );
      })
      .addCase(resetMFA.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
      })
      .addCase(resetMFA.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.error = null;

        if (state.userById && state.userById.id === action.payload.userId) {
          state.userById.mfaEnabled = false;
        }

        const userIndex = state.allUsers.findIndex(
          (user) => user.id === action.payload.userId
        );
        if (userIndex !== -1) {
          state.allUsers[userIndex].mfaEnabled = false;
        }

        if (state.user && state.user.id === action.payload.userId) {
          state.mfaRequired = false;
          state.mfaVerified = false;
          localStorage.setItem("mfaRequired", "false");
          localStorage.setItem("mfaVerified", "false");
        }

        showNotification(
          "success",
          action.payload.message || "MFA has been reset successfully"
        );
      })
      .addCase(resetMFA.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.error = action.payload?.message;
        showNotification(
          "error",
          action.payload?.message || "Failed to reset MFA"
        );
      })
      .addCase(getPasswordRequirements.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPasswordRequirements.fulfilled, (state, action) => {
        state.loading = false;
        state.passwordRequirements = action.payload || [];
      })
      .addCase(getPasswordRequirements.rejected, (state, action) => {
        state.loading = false;
        console.warn("Failed to fetch password requirements:", action.payload?.message || "Unknown error");
      });
  },
});

export const { logout, clearOtpState, updateMfaStatus, setUser } = userSlice.actions;

export const userState = (state) => state.userState;
export const allUsers = (state) => state.userState.allUsers;
export const getUserByIdFromSlice = (state) => state.userState.userById;
export const selectUserById = (state, userId) =>
  state.userState?.allUsers?.find((user) => user?.id === userId);
export const managerList = (state) => state.userState.managerList;
export const getFilteredUsersByDept_and_Role = (state) =>
  state.userState.usersByDept_and_role;
export const ManagerByDept = (state) => state.userState.managerBydept;
export const changePasswordValidate = (state) => state.userState;
export const selectMfaRequired = (state) => state.userState.mfaRequired;
export const selectMfaVerified = (state) => state.userState.mfaVerified;
export const selectPasswordRequirements = (state) => state.userState.passwordRequirements;

export default userSlice.reducer;